import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

const orderOf = (item) => Number(item?.order ?? 0);


export default function useMenuTree(menus) {
  const enabledItems = useMemo(() => {
    if (!Array.isArray(menus)) return [];
    return menus.filter((item) => item?._id && item.status !== false);
  }, [menus]);

  const topLevelItems = useMemo(
    () =>
      enabledItems
        .filter((item) => !item.parent_id)
        .sort((a, b) => orderOf(a) - orderOf(b)),
    [enabledItems],
  );

  const getChildren = useMemo(() => {
    const childrenByParentId = new Map();
    enabledItems.forEach((item) => {
      if (!item.parent_id) return;
      const siblings = childrenByParentId.get(item.parent_id) ?? [];
      siblings.push(item);
      childrenByParentId.set(item.parent_id, siblings);
    });
    childrenByParentId.forEach((siblings) => siblings.sort((a, b) => orderOf(a) - orderOf(b)));

    return (parentId) => childrenByParentId.get(parentId) ?? [];
  }, [enabledItems]);

  const getItemById = useMemo(() => {
    const itemsById = new Map(enabledItems.map((item) => [item._id, item]));
    return (id) => itemsById.get(id) ?? null;
  }, [enabledItems]);

  return { topLevelItems, getChildren, getItemById};
}

export function useSecondaryMenu() {
  
  const menus = useSelector((state) => state.menu.roleWiseData.menus, shallowEqual);
  const activeMenuId = useSelector((state) => state.secondaryMenu.activeMenuId);
  const isSecondaryMenuOpen = useSelector((state) => state.secondaryMenu.isSecondaryMenuOpen);

  const { getChildren, getItemById } = useMenuTree(menus);

  const selectedMenu = useMemo(
    () => (activeMenuId ? getChildren(activeMenuId) : []),
    [activeMenuId, getChildren],
  );

  const parentMenuItem = useMemo(
    () => (activeMenuId ? getItemById(activeMenuId) : null),
    [activeMenuId, getItemById],
  );

  return { selectedMenu, parentMenuItem, isSecondaryMenuOpen, activeMenuId };
}